import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Badge from "components/ui/badge/Badge";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import { CopyableField } from "components/ui/copyable_field/CopyableField";
import Tooltip from "components/ui/tooltip/Tooltip";
import {
  canEditRedirectUris,
  hasRealRedirectUri,
  type McpClient,
  type McpClientCreatedBy,
  type McpClientWithSecret,
  TOKEN_ENDPOINT_AUTH_METHOD_LABELS,
  tokenEndpointAuthMethodOf,
} from "lib/hooks/api/auth/mcp/schemas";
import { formatDate } from "lib/utils";
import { ChangeAuthMethodModal } from "./ChangeAuthMethodModal";
import { RevokeCredentialModal } from "./RevokeCredentialModal";
import { RotateSecretModal } from "./RotateSecretModal";
import { SetCallbackUrlModal } from "./SetCallbackUrlModal";

const DetailLabel = styled(Text)({
  minWidth: 110,
});

function createdByLabel(createdBy: McpClientCreatedBy | undefined): string {
  switch (createdBy?.type) {
    case "member":
      return createdBy.name || "A team member";
    case "former_member":
      return "A former user";
    case "stotles":
      return "Stotles";
    default:
      return "Unknown";
  }
}

/** A current member's email shows on hover; nobody else has one to show. */
function CreatedBy({ createdBy }: { createdBy: McpClientCreatedBy | undefined }) {
  const label = <Text>{createdByLabel(createdBy)}</Text>;
  if (createdBy?.type !== "member" || !createdBy.email) return label;
  return <Tooltip content={createdBy.email}>{label}</Tooltip>;
}

type Props = {
  credential: McpClient;
  /** False for a member of a company that keeps credentials to its admins. */
  canManage: boolean;
  onSecretRotated: (credential: McpClientWithSecret) => void;
};

export function McpCredentialCard({ credential, canManage, onSecretRotated }: Props) {
  const [showCallbackUrlModal, setShowCallbackUrlModal] = useState(false);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showAuthMethodModal, setShowAuthMethodModal] = useState(false);

  const isConnected = hasRealRedirectUri(credential);
  const isEditable = canEditRedirectUris(credential);
  const authMethod = tokenEndpointAuthMethodOf(credential);

  return (
    <>
      <Card borderRadius={8} padding={16}>
        <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={16}>
          <Flex column gap={8} flexGrow={1}>
            <Flex gap={8} alignItems="center">
              <Text h3>{credential.client_name ?? "MCP credential"}</Text>
              <Badge fitContent badgeType={isConnected ? "success" : "warning"}>
                {isConnected ? "Callback URL set" : "Needs callback URL"}
              </Badge>
            </Flex>

            <Flex gap={8} alignItems="center" width="100%">
              <DetailLabel description>Client ID</DetailLabel>
              <CopyableField
                value={credential.client_id}
                toastMessage="Client ID copied to clipboard"
                wrapValue
              />
            </Flex>

            {isConnected && (
              <Flex gap={8} alignItems="flex-start">
                <DetailLabel description>
                  {credential.redirect_uris.length > 1 ? "Callback URLs" : "Callback URL"}
                </DetailLabel>
                <Flex column gap={2}>
                  {credential.redirect_uris.map((uri) => (
                    <Text key={uri}>{uri}</Text>
                  ))}
                </Flex>
              </Flex>
            )}

            {canManage && !isEditable && (
              <Text description>
                This credential has more than one callback URL, which this page can&apos;t change
                without dropping the others. Ask support to update them.
              </Text>
            )}

            <Flex gap={8} alignItems="center">
              <DetailLabel description>Authentication</DetailLabel>
              <Text>
                {authMethod
                  ? `${TOKEN_ENDPOINT_AUTH_METHOD_LABELS[authMethod]} (${authMethod})`
                  : credential.token_endpoint_auth_method}
              </Text>
            </Flex>

            {credential.client_id_issued_at !== undefined && (
              <Flex gap={8} alignItems="center">
                <DetailLabel description>Created</DetailLabel>
                <Text>{formatDate(new Date(credential.client_id_issued_at * 1000))}</Text>
              </Flex>
            )}

            <Flex gap={8} alignItems="center">
              <DetailLabel description>Created by</DetailLabel>
              <CreatedBy createdBy={credential.created_by} />
            </Flex>
          </Flex>

          <Flex gap={8} flexWrap="wrap">
            <Button
              buttonType="secondary"
              onClick={() => setShowCallbackUrlModal(true)}
              disabled={!canManage || !isEditable}
            >
              {isConnected ? "Update callback URL" : "Add callback URL"}
            </Button>
            <Button
              buttonType="secondary"
              onClick={() => setShowAuthMethodModal(true)}
              disabled={!canManage || !authMethod}
            >
              Change authentication
            </Button>
            <Button
              buttonType="secondary"
              onClick={() => setShowRotateModal(true)}
              disabled={!canManage}
            >
              Rotate secret
            </Button>
            {/* Tertiary rather than secondary: a secondary destructive turns only the border red and
                leaves the label the same colour as its neighbours, which reads as a mismatch rather
                than a warning. This colours the label instead. */}
            <Button
              buttonType="tertiary"
              buttonStyle="destructive"
              onClick={() => setShowRevokeModal(true)}
              disabled={!canManage}
            >
              Revoke
            </Button>
          </Flex>
        </Flex>
      </Card>

      {showCallbackUrlModal && (
        <SetCallbackUrlModal
          credential={credential}
          onClose={() => setShowCallbackUrlModal(false)}
        />
      )}
      {showAuthMethodModal && authMethod && (
        <ChangeAuthMethodModal
          credential={credential}
          currentMethod={authMethod}
          onClose={() => setShowAuthMethodModal(false)}
        />
      )}
      {showRotateModal && (
        <RotateSecretModal
          credential={credential}
          onClose={() => setShowRotateModal(false)}
          onRotated={(rotated) => {
            setShowRotateModal(false);
            onSecretRotated(rotated);
          }}
        />
      )}
      {showRevokeModal && (
        <RevokeCredentialModal credential={credential} onClose={() => setShowRevokeModal(false)} />
      )}
    </>
  );
}
